import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Popover, Tooltip } from 'antd';
import {
  InvitePractitioner,
  InviteUser,
} from 'src/components/groupComponents/inviteUser';
import { useFeatureEntitlements } from '@frontegg/react';
import { Button } from '@insight-health-ai/roseate/components';
import {
  useGetCurrentPractitioner,
  useGetCurrentPractitionerEntitlements,
} from 'src/hooks/settings';
import { ProTrialCountDown } from 'src/components/groupComponents/proTrialCountdown';
import { AppNavMenu, useAppNavMenus } from '..';
import { RippleDot } from 'src/components';
import { UserMenu } from './userMenu';
import { WorkspaceLogo, WorkspacePlan } from './workspaceLogo';
import { useTheme } from '@insight-health-ai/roseate/theme';

const IconHalo = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  font-size: ${({ theme }) => theme.fontSize.lg['0']};
  background-color: ${({ theme }) => theme.colors.secondary[50]};
  color: ${({ theme }) => theme.colors.secondary[500]};
`;

const MenuLabel = styled.div<{ isActive: boolean }>`
  font-size: 0.7rem;
  text-align: center;
  max-width: 56px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 2px;

  ${({ isActive, theme }) =>
    isActive
      ? `
    ${IconHalo} {
      background-color: ${theme.colors.primary[100]};
      color: ${theme.colors.primary[800]};
      box-shadow: ${theme.boxShadow.inner};
    }
    ${MenuLabel} {
      color: ${theme.colors.primary[800]};
    }
  `
      : ''}

  &:hover {
    ${IconHalo} {
      font-size: ${({ theme }) => theme.fontSize.xl['0']};
    }
    ${({ isActive, theme }) =>
      isActive
        ? ``
        : `
        ${IconHalo} {
          background-color: ${theme.colors.secondary[200]};
          color: unset;
        }
      `}
  }
  &:empty {
    display: none;
  }
`;

const WithBadge = ({
  type,
  children,
}: {
  type: 'dot' | undefined;
  children: React.ReactNode;
}) => {
  if (type === 'dot') {
    return (
      <Badge
        offset={[-5, 5]}
        count={<RippleDot size="sm" appearance="alternate" />}
      >
        {children}
      </Badge>
    );
  }
  return children;
};

const MenuItem = ({ menu }: { menu: AppNavMenu }) => {
  const { tourPopup, onClick, path, submenu, title, icon, showBadge } = menu;
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const menuSegments = path.split('/').filter(Boolean);
  const isActive =
    menuSegments.length > 0 &&
    menuSegments.every((segment, index) => pathSegments[index] === segment);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (path) {
      navigate(path);
    }
  };

  let menuItem = (
    <span tabIndex={0}>
      <WithBadge type={showBadge}>
        <ItemWrapper onClick={handleClick} isActive={isActive}>
          <IconHalo isActive={isActive}>{icon}</IconHalo>
          <MenuLabel isActive={isActive}>{title}</MenuLabel>
        </ItemWrapper>
      </WithBadge>
    </span>
  );

  if (tourPopup?.open) {
    menuItem = (
      <Popover
        open={tourPopup.open}
        onOpenChange={tourPopup.onOpenChange}
        content={
          <div className="max-w-[300px]">
            {tourPopup.content}
            <div className="flex justify-end empty:hidden">
              {tourPopup.secondaryAction && (
                <Button
                  variant="outlined"
                  {...tourPopup.secondaryAction}
                  size="sm"
                />
              )}
              {tourPopup.primaryAction && (
                <Button {...tourPopup.primaryAction} size="sm" />
              )}
            </div>
          </div>
        }
        title={tourPopup.title}
        placement="right"
      >
        {menuItem}
      </Popover>
    );
  }

  if (submenu?.length) {
    return (
      <Popover
        trigger={['hover', 'focus']}
        styles={{
          body: {
            padding: theme.spacing[2],
          },
        }}
        content={
          <div className="flex flex-col gap-1 min-w-[150px]">
            <div className="text-sm font-medium pb-2 border-b border-gray-200">
              {title}
            </div>
            <div className="flex flex-col">
              {submenu.map((subItem) => {
                const { icon, title, path } = subItem;
                return (
                  <div
                    onClick={() => navigate(path)}
                    tabIndex={0}
                    key={subItem.path}
                    className="flex items-center gap-2 hover:bg-gray-200 p-2 rounded-md cursor-pointer w-full"
                  >
                    {icon}
                    {title}
                  </div>
                );
              })}
            </div>
          </div>
        }
        placement="right"
      >
        {menuItem}
      </Popover>
    );
  }

  return (
    <Tooltip title={title} placement="right">
      {menuItem}
    </Tooltip>
  );
};

export const SideNav = () => {
  const { isAdmin } = useGetCurrentPractitioner();
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: isInviteUserEntitled } =
    useFeatureEntitlements('invite-user');
  const { isEntitled: hasPhoneTriageAccess } =
    useFeatureEntitlements('phone-triage');

  const { isAuraPro, isAuraTeams, isPLGUser } =
    useGetCurrentPractitionerEntitlements();

  const menus = useAppNavMenus({ activeMenu: '' });

  const showProTrialCountDown = !isAuraPro && isPLGUser;

  const hasInviteUserAccess = isInviteUserEntitled && isAdmin;
  const showInviteUserWithMirrorOption =
    hasIntakeAccess ||
    hasFollowupAccess ||
    isAuraPro ||
    isAuraTeams ||
    hasPhoneTriageAccess;

  return (
    <div className="flex flex-col h-full overflow-hidden items-center w-[80px] flex-shrink-0 bg-white shadow-md">
      <div className="px-2 py-4 flex flex-col items-center justify-center gap-2 w-full flex-shrink-0">
        <WorkspaceLogo />
        <WorkspacePlan style={{ zoom: 0.75 }} />
      </div>
      <div className="py-2 flex flex-col items-center gap-3 w-full overflow-y-auto overflow-x-hidden">
        {menus.map((menu) => (
          <MenuItem key={menu.path} menu={menu} />
        ))}
      </div>

      <div className="px-2 py-4 mt-auto w-full flex flex-col items-center gap-2 border-t border-gray-200 flex-shrink-0">
        {showProTrialCountDown && <ProTrialCountDown isMinimized />}
        {hasInviteUserAccess &&
          (showInviteUserWithMirrorOption ? (
            <InvitePractitioner isMinimized />
          ) : (
            <InviteUser isMinimized />
          ))}
        <UserMenu />
      </div>
    </div>
  );
};
